import VueCookie from 'vue-cookie';
import {
  assessmentModuleOneData,
  assessmentModuleTwoData,
  assessmentModuleVariantLink,
  assessmentTCCData,
  assessmentResultsData,
  minimumAssessmentsData,
} from '@/store/queries/westerosGreathallQueries';
import {
  WESTEROS_AUTHORIZATION_ENUM,
  WESTEROS_MICROSERVICES_ENUM,
  westerosQuery,
} from '@/helpers/westeros';
import { dexQuery } from '@/helpers/graphql';
import { getExamGroupDetails } from '@/store/queries/dexQueries';
import {
  resetDexAssignment,
  sendAssessmentReplyFile,
  sendFinalTCC,
  UploadFileInput,
  sendTCCTheme,
  sendPartialTCC,
  UploadReplyInput,
  requestTCCRevaluation,
  startTCCRetry,
} from '@/store/mutations/westerosGreathallMutation';

const d = VueCookie.get('d', { domain: '.descomplica.com.br' });

export default class Assessments {
  static async minimunAssessmentsData(dexCourseId: number) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      minimumAssessmentsData,
      { dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.allAvailableAssessments;
  }

  static async assessmentResultsData(assessmentId: number, dexCourseId: number) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      assessmentResultsData,
      { assessmentId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.getAssessmentById;
  }

  static async assessmentModuleOneData(assessmentId: number, dexCourseId: number) {
    const responseWesteros = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      assessmentModuleOneData,
      { assessmentId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    const { dexExamGroupId } = responseWesteros.data.getAssessmentById.currentVariantByUser;
    const responseDex = await dexQuery(getExamGroupDetails, { dexExamGroupId });

    return {
      westeros: responseWesteros.data.getAssessmentById,
      dex: responseDex.data.examGroupById.examGroupsExamsByExamGroupId.nodes,
    };
  }

  static async assessmentModuleOneVariantLink(assessmentId: number, dexCourseId: number) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      assessmentModuleVariantLink,
      { assessmentId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.getAssessmentById;
  }

  static async assessmentModuleTwoData(assessmentId: number, dexCourseId: number) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      assessmentModuleTwoData,
      { assessmentId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.getAssessmentById;
  }

  static async assessmentTCCData(assessmentId: number, dexCourseId: number) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      assessmentTCCData,
      { assessmentId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.getAssessmentById;
  }

  static async sendAssessmentReplyFile(dexCourseId: number, data: UploadReplyInput) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      sendAssessmentReplyFile,
      { dexCourseId, data },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.addFileReply;
  }

  static async resetDexAssignment(
    assessmentResultId: number,
    dexCourseId: number,
  ): Promise<boolean> {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      resetDexAssignment,
      { assessmentResultId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.resetDexAssignment;
  }

  static async sendFinalTCC(
    finalTCCFile: UploadFileInput,
    quizFile: UploadFileInput,
    assessmentResultId: number,
    dexCourseId: number,
  ): Promise<boolean> {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      sendFinalTCC,
      { finalTCCFile, quizFile, assessmentResultId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.sendFinalTCC;
  }

  static async sendTCCTheme(tccTheme: string, assessmentId: number, dexCourseId: number) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      sendTCCTheme,
      { tccTheme, assessmentId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.sendTCCTheme;
  }

  static async requestTCCRevaluation(
    pleadingText: string,
    assessmentResultId: number,
    dexCourseId: number,
  ) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      requestTCCRevaluation,
      { pleadingText, assessmentResultId, dexCourseId },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );
    return response.data.requestTCCRevaluation;
  }

  static async sendPartialTCC(
    assessmentResultId: number,
    dexCourseId: number,
    fileName: string,
    dataUrl: string,
  ) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      sendPartialTCC,
      { assessmentResultId, dexCourseId, fileName, dataUrl },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.sendPartialTCC;
  }

  static async startTCCRetry(assessmentId: number, dexCourseId: number, useSameTheme: boolean) {
    const response = await westerosQuery(
      WESTEROS_MICROSERVICES_ENUM.GREATHALL,
      startTCCRetry,
      { assessmentId, dexCourseId, useSameTheme },
      WESTEROS_AUTHORIZATION_ENUM.DEX,
      { d },
    );

    return response.data.startTCCRetry;
  }
}
